<template>
  <div class="template-strategy" data-testid="template-strategy">
    <Heading class="template-strategy__title" :level="2">{{ title }}</Heading>
    <BaseText size="md">{{ description }}</BaseText>
    <ButtonSecondary
      v-if="hasButton"
      class="template-strategy__button"
      :label="buttonLabel"
      icon="EA0230"
      :loading="isDownloadingReport"
      @click="handleDownloadReport"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { ButtonSecondary, BaseText, Heading } from '@warrenbrasil/nebraska-web';

import { ITemplateStrategyData } from './types';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { forceDownloadHref } from '@/modules/common/helpers/force-download';

interface Props {
  data: ITemplateStrategyData;
}
const props = defineProps<Props>();

const isDownloadingReport = ref(false);

const title = computed(() => {
  return props.data?.title || '-';
});

const description = computed(() => {
  return props.data?.description || '-';
});

const hasButton = computed(() => {
  return props.data?.report?.url;
});

const buttonLabel = computed(() => {
  return props.data?.report?.label || '';
});

const handleDownloadReport = async () => {
  try {
    isDownloadingReport.value = true;

    const url = props.data?.report?.url;
    const filename = props.data?.report?.filename ?? 'Relatório';

    await downloadResource(url || '', filename);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um problema ao baixar o relatório. Tente novamente mais tarde.',
      type: NotificationTypes.error
    });
  } finally {
    isDownloadingReport.value = false;
  }
};

const downloadResource = async (url: string, filename: string) => {
  const data = await fetch(url, {
    headers: new Headers({
      Origin: location.origin
    }),
    mode: 'cors'
  });

  const blob = await data.blob();

  const blobUrl = window.URL.createObjectURL(blob);
  forceDownloadHref(blobUrl, filename);
};
</script>

<style lang="less" scoped>
.template-strategy {
  padding: 0 @size-spacing-x400;
  margin: 0 auto;
  max-width: 832px;

  &__title {
    margin-bottom: @size-spacing-x500;
  }

  &__button {
    margin-top: @size-spacing-x400;
  }
}
</style>
